import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  CopyButton,
  Divider,
  FileButton,
  Group,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowUpRight,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandMeta,
  IconBrandWhatsapp,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCircleCheck,
  IconCircleX,
  IconCopy,
  IconHistory,
  IconQrcode,
  IconRefresh,
  IconShieldLock,
  IconWebhook,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { trpc } from '../../lib/trpc';
import { SecretField } from '../../components/config/SecretField';
import { PermissionGate } from '../../components/PermissionGate';
import { OutgoingWebhookPanel } from '../../components/webhooks/OutgoingWebhookPanel';
import { DeliveryLogPanel } from '../../components/webhooks/DeliveryLogPanel';
import { WhatsAppQrModal } from '../../components/whatsapp/WhatsAppQrModal';
import { AdvancedSection } from '../../components/common/AdvancedSection';
import { EmptyState } from '../../components/common/EmptyState';
import { QueryError } from '../../components/common/QueryError';

type CredentialSource = 'embedded_signup' | 'manual' | 'user_oauth' | 'coexistence';

/** Specific reason Embedded Signup / Coexistence can't run yet, so the vendor
 *  (or an admin looking over their shoulder) doesn't have to guess why the
 *  button failed. Mirrors `onboardingMethods.configReason` from the API. */
function esConfigErrorMessage(reason: 'ok' | 'no_app' | 'no_config_id' | undefined): string {
  if (reason === 'no_config_id') {
    return "Embedded Signup isn't configured yet — the platform admin needs to set the Embedded Signup Config ID in Central → Configuration → WhatsApp & Meta.";
  }
  // 'no_app' or unknown/missing reason.
  return "Embedded Signup isn't configured yet — the platform admin needs to set the Meta App ID/Secret in Central → Configuration → WhatsApp & Meta.";
}

interface WaNumberRow {
  id: number;
  uid: string;
  wabaId: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  verifiedName: string | null;
  qualityRating: string | null;
  source: CredentialSource;
  status: 'pending_verification' | 'active' | 'flagged' | 'disconnected';
  webhookSubscribed: boolean;
  connectedAt: Date | string | null;
}

interface FbPageRow {
  id: number;
  uid: string;
  pageId: string;
  pageName: string;
  category: string | null;
  profilePictureUrl: string | null;
  source: CredentialSource;
  connectedAt: Date | string | null;
  webhookSubscribedAt: Date | string | null;
}

interface IgAccountRow {
  id: number;
  uid: string;
  igUserId: string;
  username: string;
  name: string | null;
  profilePictureUrl: string | null;
  source: CredentialSource;
  tokenSource: string | null;
  connectedAt: Date | string | null;
  webhookSubscribedAt: Date | string | null;
}

/**
 * Unified Meta connectivity page.
 *
 *   Tabs: WhatsApp | Facebook | Instagram
 *   Per tab: ES (recommended) button + collapsible "Enter credentials manually"
 *            + list of connected entities.
 *
 * The Embedded Signup flow leaves and returns via Meta — when we land back
 * we read `?status=...` and show a banner. The stale tRPC `meta.status`
 * query is invalidated so the connected list updates without a manual reload.
 */

/**
 * Turn the `?status=` slug the OAuth callback redirects with into something a
 * vendor can act on. Unknown slugs fall through to the raw value so a new
 * backend status is still visible rather than swallowed.
 */
function describeConnectStatus(status: string): string {
  if (status.startsWith('missing-permission:')) {
    const scopes = status.slice('missing-permission:'.length).split(',').filter(Boolean);
    return `Connect cancelled — Meta didn't grant ${scopes.join(', ')}. Start again and leave every permission ticked on the Meta screen.`;
  }
  const messages: Record<string, string> = {
    'long-lived-exchange-failed':
      "Meta wouldn't issue a lasting access token. Check the Meta App ID and secret, then try again.",
    'token-invalid': 'Meta rejected the access token. Start the connection again.',
    'code-exchange-failed': "Meta wouldn't complete the sign-in. Start the connection again.",
    'oauth-not-configured': 'No Meta App is configured yet — ask your administrator to add one.',
    'instagram-login-not-configured':
      'No Instagram App is configured yet — ask your administrator to add one.',
    'state-expired': 'The connection took too long and expired. Start again.',
    'state-corrupt': 'Something went wrong mid-connection. Start again.',
    'vendor-mismatch': 'You signed into a different workspace mid-connection. Start again.',
    'missing-code-or-state': 'Meta sent an incomplete response. Start the connection again.',
  };
  return messages[status] ?? `Meta connect: ${status}`;
}

/**
 * Connect handler for the Messenger / Instagram panels.
 *
 * Prefers the Facebook Login for Business popup when an admin has saved a
 * per-channel Configuration ID, because it keeps the vendor on the page and
 * pins the asset + permission set in the Meta App Dashboard rather than in our
 * scope list. With no Configuration ID — or if the JS SDK can't load — it falls
 * back to the server-side redirect OAuth, which is what shipped before and
 * still works.
 */
function useChannelConnect(channel: 'facebook' | 'instagram', onConnected: () => void) {
  const onboarding = trpc.integrations.meta.onboardingMethods.useQuery();
  const [popupPending, setPopupPending] = useState(false);

  const redirect = trpc.integrations.meta.startEmbeddedSignup.useMutation({
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const complete = trpc.integrations.meta.channelSignupComplete.useMutation({
    onSuccess: ({ connected }) => {
      notifications.show({
        message: `Connected ${connected} ${channel === 'facebook' ? 'page' : 'account'}${connected === 1 ? '' : 's'}`,
        color: 'green',
      });
      onConnected();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const configId =
    channel === 'facebook' ? onboarding.data?.facebookConfigId : onboarding.data?.instagramConfigId;
  const appId = onboarding.data?.appId;

  const connect = () => {
    if (!configId || !appId) {
      redirect.mutate({ channel });
      return;
    }
    setPopupPending(true);
    void import('../../lib/meta-embedded-signup')
      .then(({ launchBusinessLogin }) =>
        launchBusinessLogin({
          appId,
          configId,
          graphVersion: onboarding.data?.graphVersion ?? 'v25.0',
        }),
      )
      .then(({ code }) => complete.mutateAsync({ channel, code }))
      .catch((err: unknown) => {
        notifications.show({
          message: err instanceof Error ? err.message : 'Connection failed',
          color: 'red',
        });
      })
      .finally(() => setPopupPending(false));
  };

  return { connect, isPending: redirect.isPending || complete.isPending || popupPending };
}

function ConnectMeta() {
  const search = useSearch({ strict: false }) as { status?: string };
  const status = trpc.integrations.meta.status.useQuery();
  // onboardingMethods drives the connect buttons (appId/configId/coexistence).
  // It's batched with `status`, so if the API is unreachable both fail together
  // — surface that as one clear banner instead of silently disabled buttons.
  const onboarding = trpc.integrations.meta.onboardingMethods.useQuery();
  const utils = trpc.useUtils();

  // Distinguish "backend unreachable" from a normal empty/unconfigured state so
  // the page can explain why connect actions are unavailable.
  const backendUnreachable = status.isError || onboarding.isError;
  const retryMetaQueries = () => {
    void utils.integrations.meta.status.invalidate();
    void utils.integrations.meta.onboardingMethods.invalidate();
  };

  useEffect(() => {
    if (!search.status) return;
    void utils.integrations.meta.status.invalidate();
    if (search.status.endsWith('-connected')) {
      notifications.show({
        message: `${search.status.replace('-connected', '')} connected via Embedded Signup`,
        color: 'green',
      });
    } else {
      notifications.show({ message: describeConnectStatus(search.status), color: 'red' });
    }
    // Strip status from the URL so a refresh doesn't re-toast.
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('status');
      window.history.replaceState({}, '', url.toString());
    }
  }, [search.status, utils]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={2}>Channels</Title>
            <Text c="dimmed" size="sm">
              WhatsApp, Messenger and Instagram accounts connected to this workspace.
            </Text>
          </Stack>
          <Badge variant="light" leftSection={<IconBrandMeta size={12} />}>
            Tech Provider
          </Badge>
        </Group>

        {backendUnreachable && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            title="Can't reach the server"
            withCloseButton={false}
          >
            <Stack gap="xs">
              <Text size="sm">
                The backend isn&apos;t responding, so connection details couldn&apos;t load. Connect
                actions are disabled until this recovers. If this persists, the app may be served
                from a domain that isn&apos;t proxying <code>/api</code> to the API.
              </Text>
              <Group>
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  leftSection={<IconRefresh size={14} />}
                  loading={status.isFetching || onboarding.isFetching}
                  onClick={retryMetaQueries}
                >
                  Retry
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {!backendUnreachable && !status.isPending && (
          <ConnectionHealthStrip
            whatsapp={(status.data as { whatsapp?: WaNumberRow[] } | undefined)?.whatsapp ?? []}
            facebook={(status.data as { facebook?: FbPageRow[] } | undefined)?.facebook ?? []}
            instagram={(status.data as { instagram?: IgAccountRow[] } | undefined)?.instagram ?? []}
          />
        )}

        <Tabs defaultValue="whatsapp" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="whatsapp" leftSection={<IconBrandWhatsapp size={14} />}>
              WhatsApp
            </Tabs.Tab>
            <Tabs.Tab value="facebook" leftSection={<IconBrandFacebook size={14} />}>
              Facebook
            </Tabs.Tab>
            <Tabs.Tab value="instagram" leftSection={<IconBrandInstagram size={14} />}>
              Instagram
            </Tabs.Tab>
            <Tabs.Tab value="outgoing" leftSection={<IconArrowUpRight size={14} />}>
              Outgoing webhooks
            </Tabs.Tab>
            <Tabs.Tab value="log" leftSection={<IconHistory size={14} />}>
              Delivery log
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="whatsapp" pt="md">
            <WhatsAppPanel
              numbers={(status.data as { whatsapp?: WaNumberRow[] } | undefined)?.whatsapp ?? []}
              loading={status.isPending}
              error={status.error?.message}
              onChange={() => void utils.integrations.meta.status.invalidate()}
              hasReceivedFirstMessage={(status.data as any)?.hasReceivedFirstMessage}
              isWebhookVerified={(status.data as any)?.isWebhookVerified}
            />
          </Tabs.Panel>
          <Tabs.Panel value="facebook" pt="md">
            <FacebookPanel
              pages={(status.data as { facebook?: FbPageRow[] } | undefined)?.facebook ?? []}
              loading={status.isPending}
              error={status.error?.message}
              onChange={() => void utils.integrations.meta.status.invalidate()}
            />
          </Tabs.Panel>
          <Tabs.Panel value="instagram" pt="md">
            <InstagramPanel
              accounts={
                (status.data as { instagram?: IgAccountRow[] } | undefined)?.instagram ?? []
              }
              loading={status.isPending}
              error={status.error?.message}
              onChange={() => void utils.integrations.meta.status.invalidate()}
            />
          </Tabs.Panel>
          <Tabs.Panel value="outgoing" pt="md">
            <OutgoingWebhookPanel />
          </Tabs.Panel>
          <Tabs.Panel value="log" pt="md">
            <DeliveryLogPanel />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </motion.div>
  );
}

// ─── WhatsApp ─────────────────────────────────────────────────────────────

// accessToken is `optional()` here because the form supports two modes:
//  - Initial connect → token is required (validated in onSubmit against
//    `cfg.hasNumber`, since the requirement depends on UI state Zod can't see).
//  - Edit existing → leave blank to keep the previously-saved encrypted token
//    server-side. The API mirrors this with `accessToken: z.string().optional()`.
const WaSchema = z.object({
  wabaId: z.string().min(1, 'Business account ID is required'),
  phoneNumberId: z.string().min(1, 'Phone number ID is required'),
  accessToken: z.string().optional(),
});
type WaForm = z.infer<typeof WaSchema>;

/** One channel's roll-up for the health strip. */
interface ChannelHealth {
  label: string;
  connected: number;
  broken: number;
}

/**
 * Answers "is everything working?" without making the vendor open three tabs
 * and read three different card layouts. A channel counts as broken when it is
 * connected but not actually able to receive — the state that silently loses
 * customer messages.
 */
function ConnectionHealthStrip({
  whatsapp,
  facebook,
  instagram,
}: {
  whatsapp: WaNumberRow[];
  facebook: FbPageRow[];
  instagram: IgAccountRow[];
}) {
  const channels: ChannelHealth[] = [
    {
      label: 'WhatsApp',
      connected: whatsapp.length,
      broken: whatsapp.filter((n) => n.status !== 'active' || !n.webhookSubscribed).length,
    },
    {
      label: 'Messenger',
      connected: facebook.length,
      broken: facebook.filter((p) => !p.webhookSubscribedAt).length,
    },
    {
      label: 'Instagram',
      connected: instagram.length,
      broken: instagram.filter((a) => !a.webhookSubscribedAt).length,
    },
  ].filter((c) => c.connected > 0);

  // Nothing connected yet — the per-tab empty states already say what to do.
  if (channels.length === 0) return null;

  const needsAttention = channels.filter((c) => c.broken > 0);

  return (
    <Card withBorder padding="sm">
      {needsAttention.length === 0 ? (
        <Group gap="xs">
          <IconCircleCheck size={18} color="var(--mantine-color-green-filled)" />
          <Text size="sm" fw={500}>
            All channels working
          </Text>
          <Text size="sm" c="dimmed">
            {channels.map((c) => c.label).join(' · ')}
          </Text>
        </Group>
      ) : (
        <Group gap="xs">
          <IconAlertCircle size={18} color="var(--mantine-color-orange-filled)" />
          <Text size="sm" fw={500}>
            {needsAttention.map((c) => c.label).join(' and ')} needs attention
          </Text>
          <Text size="sm" c="dimmed">
            Incoming messages aren&apos;t reaching you. Open the tab below to fix it.
          </Text>
        </Group>
      )}
    </Card>
  );
}

function WhatsAppPanel(props: {
  numbers: WaNumberRow[];
  loading: boolean;
  error: string | undefined;
  onChange: () => void;
  hasReceivedFirstMessage?: boolean;
  isWebhookVerified?: boolean;
}) {
  const utils = trpc.useUtils();
  const cfg = trpc.integrations.meta.getWhatsAppManualConfig.useQuery();
  const onboarding = trpc.integrations.meta.onboardingMethods.useQuery();
  const _start = trpc.integrations.meta.startEmbeddedSignup.useMutation({
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  // Embedded Signup via the FB JS SDK popup — the only path that supports
  // coexistence (linking an existing WhatsApp Business app number). Posts the
  // popup's auth code + captured waba_id to embeddedSignupComplete.
  const completeEs = trpc.integrations.meta.embeddedSignupComplete.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'WhatsApp number connected', color: 'green' });
      void utils.integrations.meta.getWhatsAppManualConfig.invalidate();
      props.onChange();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const [coexistenceBusy, setCoexistenceBusy] = useState(false);
  const [newNumberBusy, setNewNumberBusy] = useState(false);

  const launchNewNumber = async () => {
    const o = onboarding.data;
    if (!o?.appId || !o.configId) {
      notifications.show({ message: esConfigErrorMessage(o?.configReason), color: 'red' });
      return;
    }
    setNewNumberBusy(true);
    try {
      const { launchEmbeddedSignup } = await import('../../lib/meta-embedded-signup');
      const result = await launchEmbeddedSignup({
        appId: o.appId,
        configId: o.configId,
        graphVersion: o.graphVersion,
      });
      await completeEs.mutateAsync({
        code: result.code,
        wabaId: result.wabaId,
        phoneNumberId: result.phoneNumberId,
        businessId: result.businessId,
        mode: 'new',
      });
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : 'Embedded Signup failed',
        color: 'red',
      });
    } finally {
      setNewNumberBusy(false);
    }
  };

  const launchCoexistence = async () => {
    const o = onboarding.data;
    if (!o?.appId || !o.configId) {
      notifications.show({ message: esConfigErrorMessage(o?.configReason), color: 'red' });
      return;
    }
    setCoexistenceBusy(true);
    try {
      const { launchEmbeddedSignup } = await import('../../lib/meta-embedded-signup');
      const result = await launchEmbeddedSignup({
        appId: o.appId,
        configId: o.configId,
        graphVersion: o.graphVersion,
        featureType: o.coexistenceFeatureType,
      });
      if (!result.isAppOnboarding) {
        // Meta completed a standard connection, not the coexistence-specific
        // FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING event — the number may not
        // be eligible for coexistence yet. Don't silently tag it coexistence.
        notifications.show({
          message:
            "Meta completed a standard connection instead of linking your existing WhatsApp Business app number — it may not be eligible for coexistence yet (e.g. too new on the app). We've connected it as a regular number instead.",
          color: 'yellow',
        });
      }
      await completeEs.mutateAsync({
        code: result.code,
        wabaId: result.wabaId,
        phoneNumberId: result.phoneNumberId,
        businessId: result.businessId,
        mode: result.isAppOnboarding ? 'coexistence' : 'new',
      });
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : 'Embedded Signup failed',
        color: 'red',
      });
    } finally {
      setCoexistenceBusy(false);
    }
  };
  const connectManual = trpc.integrations.meta.connectWhatsAppManual.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'WhatsApp number saved', color: 'green' });
      void utils.integrations.meta.getWhatsAppManualConfig.invalidate();
      props.onChange();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  // Pulls latest phone-number metadata + verification status from Meta. Migrated
  // from the old /vendor/integrations/meta-hub "Sync phone numbers" button.
  const [qrOpened, setQrOpened] = useState(false);
  const syncNumbers = trpc.whatsapp.phoneNumbers.sync.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Phone numbers synced from Meta', color: 'green' });
      void utils.integrations.meta.getWhatsAppManualConfig.invalidate();
      props.onChange();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const disconnect = trpc.integrations.meta.disconnectWhatsAppNumber.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Disconnected', color: 'gray' });
      void utils.integrations.meta.getWhatsAppManualConfig.invalidate();
      props.onChange();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<WaForm>({ resolver: zodResolver(WaSchema) });

  // Prefill form fields whenever the saved config arrives or changes.
  useEffect(() => {
    if (cfg.data?.hasNumber) {
      reset({
        wabaId: cfg.data.wabaId ?? '',
        phoneNumberId: cfg.data.phoneNumberId ?? '',
        accessToken: '',
      });
    }
  }, [cfg.data?.hasNumber, cfg.data?.wabaId, cfg.data?.phoneNumberId, reset]);

  const isUpdate = !!cfg.data?.hasNumber;
  const onSubmit = handleSubmit(async (values) => {
    const token = values.accessToken?.trim() ?? '';
    if (!isUpdate && token.length < 20) {
      setError('accessToken', { message: 'Connection key is required' });
      return;
    }
    const payload = {
      wabaId: values.wabaId,
      phoneNumberId: values.phoneNumberId,
      ...(token.length > 0 ? { accessToken: token } : {}),
    };
    const r = await connectManual.mutateAsync(payload).catch(() => null);
    if (r) {
      // Keep wabaId/phoneNumberId in the form so the "current config" view
      // stays consistent; just clear the token input.
      reset({
        wabaId: values.wabaId,
        phoneNumberId: values.phoneNumberId,
        accessToken: '',
      });
    }
  });

  // Most common manual-flow gotcha: vendor used their own Meta App for the
  // System User token, but never saved that App's Secret here, so X-Hub-
  // Signature-256 verification on inbound webhooks fails and messages get
  // silently dropped (signature-verifier.ts logs but receive route 200s
  // anyway to avoid Meta retries).
  const showByocSecretWarning =
    cfg.data?.hasNumber === true &&
    cfg.data.source === 'manual' &&
    cfg.data.hasBYOCAppSecret === false;

  // Something inside Advanced needs the vendor's attention: either the missing
  // App Secret above, or a webhook that never got subscribed. NOTE: this is
  // deliberately NOT gated on `source === 'manual'`. Embedded Signup subscribes
  // webhooks for you, but when that subscribe *fails* the retry button lives in
  // here and is the only in-app way out — so a broken connection of any source
  // must be able to reach it.
  const webhookNotSubscribed = cfg.data?.hasNumber === true && cfg.data.webhookSubscribed === false;
  const needsAttention = showByocSecretWarning || webhookNotSubscribed;
  // Vendors who manage their own credentials live in here; open it for them by
  // default so editing is still one click, and keep it shut for the two-minute
  // Embedded Signup path that never needs any of it.
  const managesOwnCredentials = cfg.data?.source === 'manual' || cfg.data?.source === 'user_oauth';

  return (
    <Stack gap="md">
      {showByocSecretWarning && (
        <Alert
          color="yellow"
          icon={<IconAlertCircle size={16} />}
          title="You're not receiving messages"
        >
          <Text size="sm">
            Messages people send you aren&apos;t arriving, because your Meta app&apos;s security key
            is missing. Add it under <b>Advanced setup</b> below — in Meta you&apos;ll find it under
            Settings → Basic — and messages will start coming through.
          </Text>
        </Alert>
      )}

      <Card>
        <Stack gap="sm">
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={2}>
              <Text fw={600}>Connect a WhatsApp number</Text>
              <Text size="sm" c="dimmed">
                Recommended. Takes about two minutes — we&apos;ll set everything up for you, so
                there&apos;s nothing to configure afterwards.
              </Text>
            </Stack>
            <Button
              leftSection={<IconBrandWhatsapp size={14} />}
              loading={newNumberBusy || completeEs.isPending}
              onClick={() => void launchNewNumber()}
            >
              Start
            </Button>
          </Group>
        </Stack>
      </Card>

      {onboarding.data?.coexistenceEnabled && (
        <Card>
          <Stack gap="sm">
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={2}>
                <Text fw={600}>Keep using your current WhatsApp Business number</Text>
                <Text size="sm" c="dimmed">
                  Keep using the WhatsApp Business app on your phone <b>and</b> message at scale
                  through LeadSync — same number, history preserved. You&apos;ll scan a QR code in
                  the popup to link your number.
                </Text>
              </Stack>
              <Tooltip
                label={
                  onboarding.isError
                    ? 'Backend unreachable — try again once the server responds.'
                    : esConfigErrorMessage(onboarding.data?.configReason)
                }
                disabled={!!onboarding.data?.appId && !!onboarding.data?.configId}
              >
                {/* Use data-disabled (not the real `disabled` prop) so the
                    Tooltip still fires on hover to explain WHY it's greyed.
                    launchCoexistence() already guards the unconfigured case
                    with a notification, so a click here is safe. */}
                <Button
                  variant="light"
                  leftSection={<IconBrandWhatsapp size={14} />}
                  loading={coexistenceBusy || completeEs.isPending}
                  data-disabled={!onboarding.data?.appId || !onboarding.data?.configId}
                  onClick={() => void launchCoexistence()}
                >
                  Link my number
                </Button>
              </Tooltip>
            </Group>
            <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
              <Text size="xs">
                Your number must have been active on the WhatsApp Business app for at least 7 days.
                Keep the app installed and open it at least once every 13 days. Group chats, calls,
                and broadcast lists stay app-only; sending throughput is lower than a Cloud-API-only
                number.
              </Text>
            </Alert>
          </Stack>
        </Card>
      )}

      <AdvancedSection
        description="Only if you manage your own Meta app, or you're finishing setup by hand."
        defaultOpen={needsAttention || managesOwnCredentials}
        badge={
          needsAttention ? (
            <Badge color="yellow" variant="light" size="sm">
              Needs attention
            </Badge>
          ) : undefined
        }
      >
        <form onSubmit={onSubmit}>
          <Stack gap="sm">
            <Text fw={600} size="sm">
              Enter credentials manually
            </Text>
            <TextInput
              label="Business account ID"
              placeholder="e.g. 102937481726354"
              {...register('wabaId')}
              error={errors.wabaId?.message}
            />
            <TextInput
              label="Phone number ID"
              placeholder="e.g. 109283746518273"
              {...register('phoneNumberId')}
              error={errors.phoneNumberId?.message}
            />
            <Controller
              control={control}
              name="accessToken"
              render={({ field }) => (
                <SecretField
                  label="Connection key"
                  description={
                    isUpdate
                      ? 'Leave blank to keep the existing key; click the eye to reveal. Paste a new one to rotate.'
                      : 'From your Meta business settings. Stored encrypted.'
                  }
                  present={!!cfg.data?.hasAccessToken}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  error={errors.accessToken?.message}
                  onReveal={() =>
                    utils.integrations.meta.revealSecret
                      .fetch({ kind: 'whatsapp_token' })
                      .then((r) => r.value)
                  }
                />
              )}
            />
            <Group>
              <Button type="submit" loading={connectManual.isPending}>
                {isUpdate ? 'Update credentials' : 'Save credentials'}
              </Button>
            </Group>
          </Stack>
        </form>

        {/* Kept mounted for every source, not just `manual`: when Embedded
            Signup's automatic subscribe fails, the retry button in here is the
            vendor's only way to recover without a support ticket. */}
        {cfg.data?.hasNumber && (
          <>
            <Divider />
            <WhatsAppWebhookCard
              webhookUrl={cfg.data.webhookUrl}
              verifyToken={cfg.data.webhookVerifyToken}
              subscribed={cfg.data.webhookSubscribed}
              onSubscribed={() => {
                void utils.integrations.meta.getWhatsAppManualConfig.invalidate();
                props.onChange();
              }}
            />
          </>
        )}

        {cfg.data?.hasNumber && cfg.data.source === 'manual' && (
          <>
            <Divider />
            <MetaAppCredentialsCard
              appId={cfg.data.byocAppId ?? ''}
              hasAppSecret={cfg.data.hasBYOCAppSecret}
            />
          </>
        )}
      </AdvancedSection>

      {cfg.data?.hasNumber && <WhatsAppTestSendCard />}

      <Divider label="Connection health" labelPosition="left" />

      {props.numbers.length > 0 && (
        <Card withBorder padding="sm">
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Delivery checks
            </Text>
            <Group gap="xl">
              <Group gap={6}>
                {props.isWebhookVerified ? (
                  <IconCircleCheck size={16} color="var(--mantine-color-green-filled)" />
                ) : (
                  <IconCircleX size={16} color="var(--mantine-color-red-filled)" />
                )}
                <Text size="sm">Webhook Connected</Text>
              </Group>
              <Group gap={6}>
                {props.hasReceivedFirstMessage ? (
                  <IconCircleCheck size={16} color="var(--mantine-color-green-filled)" />
                ) : (
                  <IconCircleX size={16} color="var(--mantine-color-orange-filled)" />
                )}
                <Text size="sm">Messages Receiving</Text>
              </Group>
            </Group>

            {!props.hasReceivedFirstMessage && props.isWebhookVerified && (
              <Alert color="orange" icon={<IconAlertCircle size={16} />} mt="xs">
                <Text size="sm">
                  <b>Action Required:</b> Send a test message from your personal phone to your
                  WhatsApp Business number to verify message delivery is working.
                </Text>
              </Alert>
            )}
          </Stack>
        </Card>
      )}

      {props.error && (
        <QueryError
          message="Couldn't check your connections."
          detail={props.error}
          onRetry={props.onChange}
        />
      )}

      {!props.loading && !props.error && props.numbers.length === 0 && (
        <EmptyState
          icon={<IconBrandWhatsapp size={28} />}
          title="No WhatsApp number yet"
          description="Connect a number to start messaging customers on WhatsApp."
        />
      )}

      {props.numbers.length > 0 && (
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            Connected numbers
          </Text>
          <Group gap="xs">
            <Button
              size="xs"
              variant="light"
              color="green"
              leftSection={<IconQrcode size={12} />}
              onClick={() => setQrOpened(true)}
            >
              QR code
            </Button>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconRefresh size={12} />}
              loading={syncNumbers.isPending}
              onClick={() => syncNumbers.mutate()}
            >
              Sync from Meta
            </Button>
          </Group>
        </Group>
      )}

      <WhatsAppQrModal opened={qrOpened} onClose={() => setQrOpened(false)} />

      <Stack gap="xs">
        {props.numbers.map((n) => (
          <Card key={n.id} withBorder>
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={2}>
                <Group gap="xs">
                  <Text fw={600} ff="monospace">
                    {n.displayPhoneNumber}
                  </Text>
                  <SourceBadge source={n.source} />
                  <StatusBadge status={n.status} />
                </Group>
                <Text size="xs" c="dimmed">
                  {n.verifiedName ? `${n.verifiedName} · ` : ''}
                  Business account {n.wabaId} · Number ID {n.phoneNumberId}
                </Text>
              </Stack>
              <Button
                size="xs"
                variant="default"
                color="red"
                loading={disconnect.isPending}
                onClick={() =>
                  confirmDisconnect(n.displayPhoneNumber || 'this number', () =>
                    disconnect.mutate({ phoneNumberId: n.phoneNumberId }),
                  )
                }
              >
                Disconnect
              </Button>
            </Group>
          </Card>
        ))}
      </Stack>

      {props.numbers.length > 0 && props.numbers[0] && (
        <WhatsAppBusinessStatusCard
          phoneNumberId={props.numbers[0].phoneNumberId}
          currentName={props.numbers[0].verifiedName}
        />
      )}
    </Stack>
  );
}

/**
 * Business status / "Get verified" card — read-only onboarding status (green
 * tick, display-name review, number registration, WABA review) plus the two
 * actionable bits: request a display-name change and set the profile photo.
 * Verification itself happens in Meta Business Manager (deep-linked).
 */
function WhatsAppBusinessStatusCard(props: { phoneNumberId: string; currentName: string | null }) {
  const utils = trpc.useUtils();
  const statusQ = trpc.whatsapp.accountStatus.useQuery();
  const [name, setName] = useState(props.currentName ?? '');

  const requestName = trpc.whatsapp.phoneNumbers.requestDisplayNameChange.useMutation({
    onSuccess: () => {
      notifications.show({
        message: 'Display-name change requested — Meta will review it.',
        color: 'green',
      });
      void utils.whatsapp.accountStatus.invalidate();
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const setPicture = trpc.whatsapp.profile.setPicture.useMutation({
    onSuccess: () => notifications.show({ message: 'Profile photo updated', color: 'green' }),
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });

  const onPickFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result).split(',')[1] ?? '';
      if (base64) setPicture.mutate({ base64, mimeType: file.type, filename: file.name });
    };
    reader.readAsDataURL(file);
  };

  const s = statusQ.data;
  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>Business status</Text>
          <Button
            component="a"
            href="https://business.facebook.com/settings/security"
            target="_blank"
            rel="noreferrer noopener"
            size="xs"
            variant="light"
            leftSection={<IconShieldLock size={14} />}
          >
            Get verified on Meta
          </Button>
        </Group>

        {statusQ.isPending ? (
          <Text size="sm" c="dimmed">
            Loading status…
          </Text>
        ) : !s ? (
          <Text size="sm" c="dimmed">
            Status unavailable — connect a number first.
          </Text>
        ) : (
          <Group gap="xs" wrap="wrap">
            <Badge
              color={s.isOfficialBusinessAccount ? 'green' : 'gray'}
              variant="light"
              leftSection={s.isOfficialBusinessAccount ? <IconCircleCheck size={12} /> : undefined}
            >
              {s.isOfficialBusinessAccount ? 'Official Business Account' : 'No green tick yet'}
            </Badge>
            {s.nameStatus && (
              <Badge variant="light" color={s.nameStatus === 'APPROVED' ? 'green' : 'yellow'}>
                Name: {s.nameStatus}
              </Badge>
            )}
            {s.codeVerificationStatus && (
              <Badge
                variant="light"
                color={s.codeVerificationStatus === 'VERIFIED' ? 'green' : 'gray'}
              >
                Number: {s.codeVerificationStatus}
              </Badge>
            )}
            {s.qualityRating && <Badge variant="light">Quality: {s.qualityRating}</Badge>}
            {s.accountReviewStatus && (
              <Badge variant="light">Business review: {s.accountReviewStatus}</Badge>
            )}
          </Group>
        )}

        <Divider />

        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Display name
          </Text>
          <Text size="xs" c="dimmed">
            Changing the verified business name sends it to Meta for review.
            {s?.newNameStatus ? ` Pending change: ${s.newNameStatus}.` : ''}
          </Text>
          <Group gap="xs" wrap="nowrap">
            <TextInput
              style={{ flex: 1 }}
              value={name}
              maxLength={75}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="Business display name"
            />
            <Button
              size="sm"
              loading={requestName.isPending}
              disabled={!name.trim() || name.trim() === (props.currentName ?? '')}
              onClick={() =>
                requestName.mutate({
                  phoneNumberId: props.phoneNumberId,
                  verifiedName: name.trim(),
                })
              }
            >
              Request change
            </Button>
          </Group>
        </Stack>

        <Divider />

        <Group justify="space-between">
          <Stack gap={2}>
            <Text size="sm" fw={500}>
              Profile photo
            </Text>
            <Text size="xs" c="dimmed">
              Square JPG/PNG, shown on your WhatsApp business profile.
            </Text>
          </Stack>
          <FileButton onChange={onPickFile} accept="image/png,image/jpeg">
            {(btnProps) => (
              <Button {...btnProps} size="xs" variant="light" loading={setPicture.isPending}>
                Upload photo
              </Button>
            )}
          </FileButton>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Webhook configuration card — surfaced inline on the WhatsApp panel after a
 * successful manual connect so the user has the URL + verify token next to
 * the credential form (instead of having to navigate to Settings → Webhooks).
 *
 * The verify token is the inbound token Meta uses to authenticate its delivery
 * GET handshake — not a Meta API secret — so it's safe to display with a
 * Show/Copy affordance, matching the UX in `settings.webhooks.tsx`.
 */
function WhatsAppWebhookCard(props: {
  webhookUrl: string;
  /** Null until a number is saved with one. The card still renders — the
   *  connection check and the subscribe retry work without it. */
  verifyToken: string | null;
  subscribed: boolean;
  onSubscribed: () => void;
}) {
  const [showToken, setShowToken] = useState(false);
  const test = trpc.integrations.meta.webhooks.test.useMutation({
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  // Subscribes the WABA to our webhook callback at Meta. Migrated from the
  // old /vendor/integrations/meta-hub page; lives here now so the user can
  // do everything in one place.
  const subscribe = trpc.whatsapp.subscriptions.subscribeWaba.useMutation({
    onSuccess: () => {
      notifications.show({ message: "You're now receiving messages", color: 'green' });
      props.onSubscribed();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  type Step = { step: string; status: 'ok' | 'error' | 'skipped'; detail?: string; ms: number };
  const steps = (test.data ?? []) as Step[];

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <IconWebhook size={18} />
        <Title order={5}>Message delivery</Title>
        <Badge variant="light" color={props.subscribed ? 'green' : 'gray'}>
          {props.subscribed ? 'Receiving messages' : 'Not receiving messages'}
        </Badge>
      </Group>
      <Alert color="blue" variant="light" icon={<IconAlertCircle size={14} />}>
        In Meta, go to WhatsApp → Configuration → Webhook and paste these two values.
      </Alert>
      <Group align="flex-end" wrap="nowrap">
        <TextInput
          flex={1}
          label="Delivery address"
          description="Where Meta sends your incoming messages."
          readOnly
          value={props.webhookUrl}
          ff="monospace"
        />
        <CopyButton value={props.webhookUrl}>
          {({ copy, copied }) => (
            <Tooltip label={copied ? 'Copied' : 'Copy URL'}>
              <ActionIcon variant="default" size="lg" onClick={copy} mb={2}>
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
      <Group align="flex-end" wrap="nowrap">
        <TextInput
          flex={1}
          label="Security code"
          readOnly
          value={
            props.verifyToken === null
              ? '— not generated yet —'
              : showToken
                ? props.verifyToken
                : '••••••••••••••••'
          }
          ff="monospace"
          description="Meta uses this to confirm the address belongs to you."
        />
        <Button
          variant="default"
          disabled={props.verifyToken === null}
          onClick={() => setShowToken((v) => !v)}
          mb={2}
        >
          {showToken ? 'Hide' : 'Show'}
        </Button>
        <CopyButton value={props.verifyToken ?? ''}>
          {({ copy, copied }) => (
            <Tooltip label={copied ? 'Copied' : 'Copy token'}>
              <ActionIcon variant="default" size="lg" onClick={copy} mb={2}>
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
      <Group>
        <Button
          variant="default"
          leftSection={<IconRefresh size={14} />}
          loading={test.isPending}
          onClick={() => test.mutate({ channel: 'whatsapp' })}
        >
          Run a check
        </Button>
        {!props.subscribed && (
          <Button
            leftSection={<IconBrandWhatsapp size={14} />}
            loading={subscribe.isPending}
            onClick={() => subscribe.mutate()}
          >
            Start receiving messages
          </Button>
        )}
        <Text size="xs" c="dimmed">
          Checks your credentials and that your number is reachable.
        </Text>
      </Group>
      {steps.length > 0 && (
        <Stack gap={4}>
          {steps.map((s, i) => (
            <Group key={i} gap="xs" wrap="nowrap">
              {s.status === 'ok' && (
                <IconCircleCheck size={14} color="var(--mantine-color-green-filled)" />
              )}
              {s.status === 'error' && (
                <IconCircleX size={14} color="var(--mantine-color-red-filled)" />
              )}
              {s.status === 'skipped' && (
                <IconCircleX size={14} color="var(--mantine-color-gray-filled)" />
              )}
              <Text size="sm">{s.step}</Text>
              {s.detail && (
                <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
                  — {s.detail}
                </Text>
              )}
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Meta App credentials (BYOC) card — surfaced inline on the WhatsApp panel
 * because the most common manual-flow gotcha is forgetting to save the App
 * Secret here, which causes inbound webhook signature verification to fail
 * silently. Persists to `meta_app_id_byoc` + `meta_app_secret_byoc` via the
 * existing tenancy.settings.setMany endpoint (same storage as
 * `/vendor/integrations/custom-app`, so the two surfaces stay in sync).
 */
function MetaAppCredentialsCard(props: { appId: string; hasAppSecret: boolean }) {
  const utils = trpc.useUtils();
  const [appId, setAppId] = useState(props.appId);
  const [appSecret, setAppSecret] = useState('');
  // Sync from prop whenever the upstream query refetches.
  useEffect(() => {
    setAppId(props.appId);
    setAppSecret('');
  }, [props.appId, props.hasAppSecret]);

  const setMany = trpc.tenancy.settings.setMany.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Meta App credentials saved', color: 'green' });
      void utils.integrations.meta.getWhatsAppManualConfig.invalidate();
      setAppSecret('');
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const onSave = () => {
    const settings: Record<string, string | null> = {
      meta_app_id_byoc: appId.trim() || null,
    };
    // Only include the secret when the user typed one — blank means "keep
    // the existing encrypted secret" (same UX as the access-token field).
    if (appSecret.length > 0) settings['meta_app_secret_byoc'] = appSecret;
    setMany.mutate({ settings });
  };

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <IconShieldLock size={18} />
        <Title order={5}>Your Meta app</Title>
        <Badge variant="light" color={props.hasAppSecret ? 'green' : 'red'}>
          {props.hasAppSecret ? 'Complete' : 'Incomplete'}
        </Badge>
      </Group>
      <Text size="sm" c="dimmed">
        Only needed if you connected with your own Meta app. Lets us confirm that incoming messages
        really came from Meta.
      </Text>
      <TextInput
        label="App ID"
        placeholder="e.g. 1234567890123456"
        value={appId}
        onChange={(e) => setAppId(e.currentTarget.value)}
      />
      <SecretField
        label="App secret"
        description={
          props.hasAppSecret
            ? 'Leave blank to keep the existing secret; click the eye to reveal. Stored encrypted at rest.'
            : 'From Meta App Dashboard → Settings → Basic. Stored encrypted at rest.'
        }
        present={props.hasAppSecret}
        value={appSecret}
        onChange={setAppSecret}
        onReveal={() =>
          utils.integrations.meta.revealSecret
            .fetch({ kind: 'byoc_app_secret' })
            .then((r) => r.value)
        }
      />
      <Group>
        <Button loading={setMany.isPending} onClick={onSave}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * Send test message — sends the universal `hello_world` template (always
 * pre-approved on every WABA, deliverable regardless of the 24h customer
 * service window) to a vendor-supplied number. Models the WhatsJet PHP
 * "Send a Test Message" affordance: the simplest end-to-end proof that
 * token + WABA + phone-number + Meta API are all wired correctly.
 */
function WhatsAppTestSendCard() {
  const [to, setTo] = useState('');
  const send = trpc.integrations.meta.sendWhatsAppTestMessage.useMutation({
    onSuccess: () =>
      notifications.show({
        message: 'Test message sent',
        color: 'green',
      }),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  return (
    <Card>
      <Stack gap="sm">
        <Group gap="xs">
          <IconBrandWhatsapp size={18} />
          <Title order={5}>Send yourself a test</Title>
        </Group>
        <Text size="sm" c="dimmed">
          Sends a short pre-approved greeting so you can confirm everything works end to end. It
          arrives even if that person has never messaged you before.
        </Text>
        <Group align="flex-end" wrap="nowrap">
          <TextInput
            flex={1}
            label="Send to"
            description="A real WhatsApp message will be sent. Include the country code, e.g. +1 415 555 0100."
            placeholder="+14155550100"
            value={to}
            onChange={(e) => setTo(e.currentTarget.value)}
          />
          <Button
            loading={send.isPending}
            disabled={to.trim().length < 8}
            onClick={() => send.mutate({ to: to.trim() })}
            mb={2}
          >
            Send
          </Button>
        </Group>
        {send.data && (
          <Text size="xs" c="green">
            Sent. Check that phone — it should arrive in a few seconds.
          </Text>
        )}
      </Stack>
    </Card>
  );
}

// ─── Facebook ─────────────────────────────────────────────────────────────

const FbSchema = z.object({
  pageId: z.string().min(1, 'Page ID is required'),
  // Optional on re-save — blank keeps the saved token.
  pageAccessToken: z.string().optional(),
});
type FbForm = z.infer<typeof FbSchema>;

/**
 * Webhook card for Messenger / Instagram — parity with `WhatsAppWebhookCard`.
 * Connecting an account already auto-subscribes its webhook; this surfaces the
 * callback URL + verify token and exposes Subscribe / Test / Regenerate so a
 * vendor can re-subscribe, verify delivery, or wire the webhook manually in the
 * Meta App Dashboard. Backed by the existing `integrations.meta.webhooks.*`
 * procedures. The Meta App Secret used for inbound signature verification is
 * vendor-wide (shared with WhatsApp), so it lives on the WhatsApp tab.
 */
function ChannelWebhookCard(props: { channel: 'facebook' | 'instagram'; onChange: () => void }) {
  const [showToken, setShowToken] = useState(false);
  const utils = trpc.useUtils();
  const all = trpc.integrations.meta.webhooks.getAll.useQuery({ showTokens: showToken });
  const info = all.data?.find((c) => c.channel === props.channel);

  const subscribe = trpc.integrations.meta.webhooks.subscribe.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Webhook subscribed', color: 'green' });
      void utils.integrations.meta.webhooks.getAll.invalidate();
      props.onChange();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const test = trpc.integrations.meta.webhooks.test.useMutation({
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const regenerate = trpc.integrations.meta.webhooks.regenerate.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'New security code generated', color: 'green' });
      setShowToken(true);
      void utils.integrations.meta.webhooks.getAll.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  type Step = { step: string; status: 'ok' | 'error' | 'skipped'; detail?: string; ms: number };
  const steps = (test.data ?? []) as Step[];
  const ChannelIcon = props.channel === 'facebook' ? IconBrandFacebook : IconBrandInstagram;
  const dashHint =
    props.channel === 'facebook'
      ? 'Meta App Dashboard → Messenger → Settings → Webhooks'
      : 'Meta App Dashboard → Webhooks → instagram (subscribe the messages field)';

  return (
    <Card>
      <Stack gap="sm">
        <Group gap="xs">
          <IconWebhook size={18} />
          <Title order={5}>Webhook</Title>
          <Badge variant="light" color={info?.subscribed ? 'green' : 'gray'}>
            {info?.subscribed ? 'Receiving messages' : 'Not receiving messages'}
          </Badge>
        </Group>
        <Alert color="blue" variant="light" icon={<IconAlertCircle size={14} />}>
          Connecting an account auto-subscribes its webhook. Use this to re-subscribe or verify
          delivery, or to wire it manually in {dashHint}. The Meta App Secret used to verify inbound
          signatures is shared across channels — set it on the WhatsApp tab → Meta App credentials.
        </Alert>
        <Group align="flex-end" wrap="nowrap">
          <TextInput
            flex={1}
            label="Delivery address"
            description="Where Meta sends your incoming messages."
            readOnly
            value={info?.callbackUrl ?? ''}
            ff="monospace"
          />
          <CopyButton value={info?.callbackUrl ?? ''}>
            {({ copy, copied }) => (
              <Tooltip label={copied ? 'Copied' : 'Copy URL'}>
                <ActionIcon variant="default" size="lg" onClick={copy} mb={2}>
                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
        <Group align="flex-end" wrap="nowrap">
          <TextInput
            flex={1}
            label="Security code"
            readOnly
            value={
              showToken
                ? (info?.verifyToken ?? '— not set —')
                : info?.tokenSet
                  ? '••••••••••••••••'
                  : '— not set —'
            }
            ff="monospace"
            description="Meta uses this to confirm the address belongs to you."
          />
          <Button variant="default" onClick={() => setShowToken((v) => !v)} mb={2}>
            {showToken ? 'Hide' : 'Show'}
          </Button>
          <Button
            variant="default"
            leftSection={<IconRefresh size={14} />}
            loading={regenerate.isPending}
            onClick={() => regenerate.mutate({ channel: props.channel })}
            mb={2}
          >
            Regenerate
          </Button>
        </Group>
        <Group>
          <Button
            variant="default"
            leftSection={<ChannelIcon size={14} />}
            loading={subscribe.isPending}
            onClick={() => subscribe.mutate({ channel: props.channel })}
          >
            {info?.subscribed ? 'Re-subscribe webhook' : 'Subscribe webhook'}
          </Button>
          <Button
            variant="default"
            leftSection={<IconRefresh size={14} />}
            loading={test.isPending}
            onClick={() => test.mutate({ channel: props.channel })}
          >
            Run a check
          </Button>
        </Group>
        {steps.length > 0 && (
          <Stack gap={4}>
            {steps.map((s, i) => (
              <Group key={i} gap="xs" wrap="nowrap">
                {s.status === 'ok' && (
                  <IconCircleCheck size={14} color="var(--mantine-color-green-filled)" />
                )}
                {s.status === 'error' && (
                  <IconCircleX size={14} color="var(--mantine-color-red-filled)" />
                )}
                {s.status === 'skipped' && (
                  <IconCircleX size={14} color="var(--mantine-color-gray-filled)" />
                )}
                <Text size="sm">{s.step}</Text>
                {s.detail && (
                  <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
                    — {s.detail}
                  </Text>
                )}
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function FacebookPanel(props: {
  pages: FbPageRow[];
  loading: boolean;
  error: string | undefined;
  onChange: () => void;
}) {
  const start = useChannelConnect('facebook', props.onChange);
  const utils = trpc.useUtils();
  const cfg = trpc.integrations.meta.getFacebookManualConfig.useQuery();
  const connectManual = trpc.integrations.meta.connectFacebookManual.useMutation({
    onSuccess: ({ pageName }) => {
      notifications.show({ message: `Connected ${pageName}`, color: 'green' });
      props.onChange();
      void utils.integrations.meta.getFacebookManualConfig.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const disconnect = trpc.facebook.pages.disconnect.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Page disconnected', color: 'gray' });
      props.onChange();
      void utils.integrations.meta.getFacebookManualConfig.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const [showManual, setShowManual] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FbForm>({ resolver: zodResolver(FbSchema) });
  // Prefill the saved page id (visible) and clear the token field (the token is
  // never returned — a saved token shows as a masked placeholder). Auto-open the
  // manual section when there's a saved config so it's visible.
  useEffect(() => {
    if (cfg.data?.hasConfig) {
      reset({ pageId: cfg.data.pageId ?? '', pageAccessToken: '' });
      setShowManual(true);
    }
  }, [cfg.data, reset]);
  const onSubmit = handleSubmit(async (values) => {
    const token = values.pageAccessToken?.trim();
    await connectManual
      .mutateAsync({
        pageId: values.pageId.trim(),
        ...(token ? { pageAccessToken: token } : {}),
      })
      .catch(() => null);
  });

  return (
    <Stack gap="md">
      <Card>
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={2}>
            <Text fw={600}>Connect via Embedded Signup</Text>
            <Text size="sm" c="dimmed">
              Sign in with Facebook and we&apos;ll auto-connect every page you can message and
              subscribe webhooks for them.
            </Text>
          </Stack>
          <Button
            leftSection={<IconBrandFacebook size={14} />}
            loading={start.isPending}
            onClick={start.connect}
          >
            Start
          </Button>
        </Group>
      </Card>

      <Card>
        <Stack gap="xs">
          <Group
            justify="space-between"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowManual((s) => !s)}
          >
            <Group gap="xs">
              <IconShieldLock size={16} />
              <Text fw={600}>Advanced setup</Text>
            </Group>
            {showManual ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </Group>
          <Collapse expanded={showManual}>
            <form onSubmit={onSubmit}>
              <Stack gap="sm" mt="xs">
                <TextInput
                  label="Page ID"
                  placeholder="e.g. 482736451029384"
                  {...register('pageId')}
                  error={errors.pageId?.message}
                />
                <Controller
                  control={control}
                  name="pageAccessToken"
                  render={({ field }) => (
                    <SecretField
                      label="Connection key"
                      description={
                        cfg.data?.hasAccessToken
                          ? 'A token is saved. Leave blank to keep it, click the eye to reveal, or paste a new one to replace.'
                          : undefined
                      }
                      present={!!cfg.data?.hasAccessToken}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      error={errors.pageAccessToken?.message}
                      onReveal={() =>
                        utils.integrations.meta.revealSecret
                          .fetch({ kind: 'facebook_token' })
                          .then((r) => r.value)
                      }
                    />
                  )}
                />
                <Group>
                  <Button type="submit" loading={connectManual.isPending}>
                    Save credentials
                  </Button>
                </Group>
              </Stack>
            </form>
          </Collapse>
        </Stack>
      </Card>

      <ChannelWebhookCard channel="facebook" onChange={props.onChange} />

      <Divider label="Connected" labelPosition="left" />

      {props.error && (
        <QueryError
          message="Couldn't check your connections."
          detail={props.error}
          onRetry={props.onChange}
        />
      )}

      {!props.loading && !props.error && props.pages.length === 0 && (
        <EmptyState
          icon={<IconBrandFacebook size={28} />}
          title="No Facebook Page yet"
          description="Connect a Page to reply to Messenger conversations from your inbox."
        />
      )}

      <Stack gap="xs">
        {props.pages.map((p) => (
          <Card key={p.id} withBorder>
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={2}>
                <Group gap="xs">
                  <Text fw={600}>{p.pageName}</Text>
                  <SourceBadge source={p.source} />
                  {p.webhookSubscribedAt && (
                    <Badge
                      variant="light"
                      color="green"
                      leftSection={<IconCircleCheck size={10} />}
                    >
                      webhook
                    </Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  page-id {p.pageId}
                  {p.category ? ` · ${p.category}` : ''}
                </Text>
              </Stack>
              <Button
                size="xs"
                variant="default"
                color="red"
                loading={disconnect.isPending}
                onClick={() =>
                  confirmDisconnect(p.pageName || 'this Page', () =>
                    disconnect.mutate({ pageId: p.id }),
                  )
                }
              >
                Disconnect
              </Button>
            </Group>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

// ─── Instagram ────────────────────────────────────────────────────────────

const IgSchema = z.object({
  igUserId: z.string().min(1, 'Account ID is required'),
  // Optional on re-save — blank keeps the saved token.
  accessToken: z.string().optional(),
  linkedFacebookPageId: z.string().optional(),
});
type IgForm = z.infer<typeof IgSchema>;

function InstagramPanel(props: {
  accounts: IgAccountRow[];
  loading: boolean;
  error: string | undefined;
  onChange: () => void;
}) {
  const start = useChannelConnect('instagram', props.onChange);
  // Business Login for Instagram — standalone IG accounts with no Facebook Page.
  const startIgLogin = trpc.integrations.meta.startInstagramLogin.useMutation({
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const utils = trpc.useUtils();
  const cfg = trpc.integrations.meta.getInstagramManualConfig.useQuery();
  const connectManual = trpc.integrations.meta.connectInstagramManual.useMutation({
    onSuccess: ({ username }) => {
      notifications.show({ message: `Connected @${username}`, color: 'green' });
      props.onChange();
      void utils.integrations.meta.getInstagramManualConfig.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const disconnect = trpc.instagram.accounts.disconnect.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Account disconnected', color: 'gray' });
      props.onChange();
      void utils.integrations.meta.getInstagramManualConfig.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const [showManual, setShowManual] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IgForm>({ resolver: zodResolver(IgSchema) });
  // Prefill the saved IG account id + linked page (visible); token stays blank
  // (never returned — shown as a masked placeholder). Auto-open when saved.
  useEffect(() => {
    if (cfg.data?.hasConfig) {
      reset({
        igUserId: cfg.data.igUserId ?? '',
        accessToken: '',
        linkedFacebookPageId: cfg.data.linkedFacebookPageId ?? '',
      });
      setShowManual(true);
    }
  }, [cfg.data, reset]);
  const onSubmit = handleSubmit(async (values) => {
    const token = values.accessToken?.trim();
    await connectManual
      .mutateAsync({
        igUserId: values.igUserId.trim(),
        ...(token ? { accessToken: token } : {}),
        linkedFacebookPageId: values.linkedFacebookPageId?.trim() || null,
      })
      .catch(() => null);
  });

  return (
    <Stack gap="md">
      <Card>
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={2}>
            <Text fw={600}>Connect via Embedded Signup</Text>
            <Text size="sm" c="dimmed">
              Sign in with Facebook — we&apos;ll connect any Instagram Business account linked to a
              page you manage.
            </Text>
          </Stack>
          <Button
            leftSection={<IconBrandInstagram size={14} />}
            loading={start.isPending}
            onClick={start.connect}
          >
            Start
          </Button>
        </Group>
      </Card>

      <Card>
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={2}>
            <Text fw={600}>Connect a standalone Instagram account</Text>
            <Text size="sm" c="dimmed">
              Sign in with Instagram directly — no Facebook Page required. Use this for an Instagram
              professional account that isn&apos;t linked to a Facebook Page.
            </Text>
          </Stack>
          <Button
            variant="light"
            leftSection={<IconBrandInstagram size={14} />}
            loading={startIgLogin.isPending}
            onClick={() => startIgLogin.mutate()}
          >
            Connect Instagram (no Facebook Page)
          </Button>
        </Group>
      </Card>

      <Card>
        <Stack gap="xs">
          <Group
            justify="space-between"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowManual((s) => !s)}
          >
            <Group gap="xs">
              <IconShieldLock size={16} />
              <Text fw={600}>Advanced setup</Text>
            </Group>
            {showManual ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </Group>
          <Collapse expanded={showManual}>
            <form onSubmit={onSubmit}>
              <Stack gap="sm" mt="xs">
                <TextInput
                  label="Account ID"
                  placeholder="e.g. 17841405822304914"
                  {...register('igUserId')}
                  error={errors.igUserId?.message}
                />
                <Controller
                  control={control}
                  name="accessToken"
                  render={({ field }) => (
                    <SecretField
                      label="Connection key"
                      description={
                        cfg.data?.hasAccessToken
                          ? 'A token is saved. Leave blank to keep it, click the eye to reveal, or paste a new one to replace.'
                          : 'Page token (page_token flow) or IG Business Login token.'
                      }
                      present={!!cfg.data?.hasAccessToken}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      error={errors.accessToken?.message}
                      onReveal={() =>
                        utils.integrations.meta.revealSecret
                          .fetch({ kind: 'instagram_token' })
                          .then((r) => r.value)
                      }
                    />
                  )}
                />
                <TextInput
                  label="Connected Facebook Page"
                  description="Optional. Only needed if your connection key belongs to a Page."
                  {...register('linkedFacebookPageId')}
                  error={errors.linkedFacebookPageId?.message}
                />
                <Group>
                  <Button type="submit" loading={connectManual.isPending}>
                    Save credentials
                  </Button>
                </Group>
              </Stack>
            </form>
          </Collapse>
        </Stack>
      </Card>

      <ChannelWebhookCard channel="instagram" onChange={props.onChange} />

      <Divider label="Connected" labelPosition="left" />

      {props.error && (
        <QueryError
          message="Couldn't check your connections."
          detail={props.error}
          onRetry={props.onChange}
        />
      )}

      {!props.loading && !props.error && props.accounts.length === 0 && (
        <EmptyState
          icon={<IconBrandInstagram size={28} />}
          title="No Instagram account yet"
          description="Connect an account to reply to Instagram DMs from your inbox."
        />
      )}

      <Stack gap="xs">
        {props.accounts.map((a) => (
          <Card key={a.id} withBorder>
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={2}>
                <Group gap="xs">
                  <Text fw={600}>@{a.username}</Text>
                  <SourceBadge source={a.source} />
                  <Badge variant="light" color="grape">
                    {a.tokenSource ?? 'token'}
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed">
                  ig-id {a.igUserId}
                  {a.name ? ` · ${a.name}` : ''}
                </Text>
              </Stack>
              <Button
                size="xs"
                variant="default"
                color="red"
                loading={disconnect.isPending}
                onClick={() =>
                  confirmDisconnect(a.username ? `@${a.username}` : 'this account', () =>
                    disconnect.mutate({ accountId: a.id }),
                  )
                }
              >
                Disconnect
              </Button>
            </Group>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

// ─── Shared bits ──────────────────────────────────────────────────────────

/** Confirm before a disconnect, naming what stops working. */
function confirmDisconnect(what: string, onConfirm: () => void) {
  modals.openConfirmModal({
    title: `Disconnect ${what}?`,
    children: (
      <Text size="sm">
        You&apos;ll stop receiving messages sent to {what}, and you won&apos;t be able to reply to
        existing conversations. You can connect it again later.
      </Text>
    ),
    labels: { confirm: 'Disconnect', cancel: 'Keep connected' },
    confirmProps: { color: 'red' },
    onConfirm,
  });
}

function SourceBadge({ source }: { source: CredentialSource }) {
  if (source === 'embedded_signup') {
    return (
      <Badge variant="light" color="blue">
        Automatic
      </Badge>
    );
  }
  if (source === 'coexistence') {
    return (
      <Badge variant="light" color="teal">
        Linked number
      </Badge>
    );
  }
  if (source === 'manual') {
    return (
      <Badge variant="light" color="orange">
        Manual
      </Badge>
    );
  }
  return (
    <Badge variant="light" color="gray">
      Legacy
    </Badge>
  );
}

function StatusBadge({
  status,
}: {
  status: 'pending_verification' | 'active' | 'flagged' | 'disconnected';
}) {
  if (status === 'active') {
    return (
      <Badge variant="light" color="green" leftSection={<IconCircleCheck size={10} />}>
        Working
      </Badge>
    );
  }
  if (status === 'pending_verification') {
    return (
      <Badge variant="light" color="yellow">
        Pending verification
      </Badge>
    );
  }
  if (status === 'flagged') {
    return (
      <Badge variant="light" color="orange">
        Flagged by Meta
      </Badge>
    );
  }
  return (
    <Badge variant="light" color="gray" leftSection={<IconCircleX size={10} />}>
      Disconnected
    </Badge>
  );
}

function ConnectMetaGated() {
  // Gate the whole page on `manage_integrations` so members who lack it
  // see the standard "Not available" empty state instead of mounting the
  // page, firing tRPC queries, then hitting silent failures on every
  // action. Mirrors the gating already applied via the nav (route.tsx).
  return (
    <PermissionGate permission="manage_integrations">
      <ConnectMeta />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/integrations/meta')({ component: ConnectMetaGated });
